import {Dialog, DialogDisclosure, DialogHeader, DialogProvider} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Text} from '@/components/ui/text/Text'
import {useTranslation} from '@/translations/i18n'
import {View, ViewEntity} from '../../types'
import {ViewForm} from '../view-form/ViewForm'
import {useDialogContext} from '@ariakit/react'
import {errorHandler, raise} from '@/utilities/helpers'
import {CreateViewSchema} from '../../formSchemas'
import {zodResolver} from '@hookform/resolvers/zod'
import {useStoreView} from '../../queries/useStoreView'
import {toast} from '@/utilities/toast/toast'
import {AxiosError} from 'axios'
import {FormProvider, useForm} from 'react-hook-form'

export const DuplicateViewDialog = ({
    entity,
    view,
    trigger
}: {
    entity: ViewEntity
    view: View
    trigger: React.ReactElement
}) => {
    const {t} = useTranslation()

    return (
        <DialogProvider>
            <DialogDisclosure render={trigger} />
            <Dialog width="400px">
                <DialogHeader>
                    <Text render="h1" size="textXl" weight={500}>
                        {t('commons:duplicate_view')}
                    </Text>
                </DialogHeader>
                <DuplicateViewForm entity={entity} view={view} />
            </Dialog>
        </DialogProvider>
    )
}

const DuplicateViewForm = ({entity, view}: {entity: ViewEntity; view: View}) => {
    const {t} = useTranslation()
    const {setOpen} = useDialogContext() ?? raise('Dialog not found')
    const form = useForm<CreateViewSchema>({
        defaultValues: {
            name: view.name,
            visibility: view.visibility
        },
        resolver: zodResolver(CreateViewSchema)
    })

    const storeViewMutation = useStoreView({
        onSuccess: () => {
            setOpen(false)
            toast.success(t('commons:view_duplicated'))
        },
        onError: error => {
            if (error instanceof AxiosError && error.response?.data.code == 'already-existing-name') {
                form.setError('name', {message: t('errors:view_already_exists')})
            } else {
                errorHandler(error)
            }
        }
    })

    return (
        <FormProvider {...form}>
            <ViewForm
                entity={entity}
                submitLabel={t('commons:duplicate_view')}
                isPending={storeViewMutation.isPending}
                onSubmit={data => {
                    const body = new URLSearchParams(view.body)
                    body.delete('view')

                    storeViewMutation.mutate({
                        ...data,
                        entity,
                        //Since we need the view id in the search params and we don't have it until the api is success, the &view={{id}} param is sent to backend to be replaced with the actual id
                        body: body.toString() + '&view={{id}}'
                    })
                }}
            />
        </FormProvider>
    )
}
